import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function DataroomPanel({ deal, dealId, userRole }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  // Defaults to 'company' — with no explicit choice the upload behaves
  // exactly as it did before the picker existed. This selects the folder only;
  // uploadDealDocument derives visibility from it server-side.
  const [uploadFolder, setUploadFolder] = useState('company');
  const functions = getFunctions();

  const fetchDocuments = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getDealDocuments');
      const result = await fn({ dealId });
      setDocs(result.data.documents || []);
    } catch (err) {
      setError('Could not load documents');
    } finally {
      setLoading(false);
    }
  }, [dealId, functions]);

  useEffect(() => {
    if (deal?.drive_folder_id) {
      fetchDocuments();
    } else {
      setLoading(false);
    }
  }, [dealId, deal?.drive_folder_id, fetchDocuments]);

  async function handleCreateDataroom() {
    setCreating(true);
    setError('');
    setMessage('');
    try {
      const fn = httpsCallable(functions, 'createDealDataroom');
      await fn({ dealId });
      setMessage('Dataroom created successfully');
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setError(err.message || 'Failed to create dataroom');
    } finally {
      setCreating(false);
    }
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      setError('File must be under 20MB');
      return;
    }
    setUploading(true);
    setError('');
    setMessage('');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      const fn = httpsCallable(functions, 'uploadDealDocument');
      await fn({
        dealId,
        fileName: file.name,
        fileBase64: base64,
        mimeType: file.type,
        folder: uploadFolder,
        // Sent for wire-compatibility only. The server ignores it and derives
        // visibility from `folder` (functions/index.js) so a client cannot
        // mis-tag an internal document as company-visible.
        visibility: uploadFolder === 'internal' ? 'internal' : 'shared',
      });
      setMessage('File uploaded successfully');
      await fetchDocuments();
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  const isCompany = userRole === 'company';
  const hasDataroom = !!deal?.drive_folder_id;

  if (loading) {
    return (
      <div style={{ padding: '16px', color: 'var(--color-text-secondary)', fontSize: '13px' }}>
        Loading documents...
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', color: 'var(--color-text)' }}>
        Document Dataroom
      </div>

      {!hasDataroom && !isCompany && (
        <div className="card mb-4">
          <div className="card-body">
            <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '12px' }}>
              No dataroom has been created for this deal yet.
            </p>
            <button
              onClick={handleCreateDataroom}
              disabled={creating}
              style={{
                padding: '7px 16px',
                borderRadius: '6px',
                border: '1px solid var(--color-accent)',
                background: 'var(--color-accent)',
                color: '#fff',
                cursor: creating ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                opacity: creating ? 0.7 : 1,
              }}
            >
              {creating ? 'Creating...' : 'Create Dataroom'}
            </button>
          </div>
        </div>
      )}

      {!hasDataroom && isCompany && (
        <div className="alert alert-info">
          Your document dataroom has not been set up yet. Please contact your Mirepa analyst.
        </div>
      )}

      {hasDataroom && (
        <div style={{ marginBottom: '16px' }}>
          {!isCompany && (
            <div style={{ marginBottom: '10px' }}>
              <label
                htmlFor="dataroom-upload-folder"
                style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginRight: '8px' }}
              >
                Upload to
              </label>
              <select
                id="dataroom-upload-folder"
                value={uploadFolder}
                onChange={(e) => setUploadFolder(e.target.value)}
                disabled={uploading}
                style={{
                  padding: '5px 8px',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-card)',
                  color: 'var(--color-text)',
                  fontSize: '12px',
                }}
              >
                <option value="company">Company Documents (visible to the company)</option>
                <option value="internal">Internal Documents (internal team only)</option>
              </select>
            </div>
          )}
          <label style={{
            display: 'inline-block',
            padding: '7px 16px',
            borderRadius: '6px',
            border: '1px solid #c9952a',
            background: '#c9952a',
            color: '#fff',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            opacity: uploading ? 0.7 : 1,
          }}>
            {uploading ? 'Uploading...' : '+ Upload Document'}
            <input
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              disabled={uploading}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
            />
          </label>
          <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginLeft: '8px' }}>
            Max 20MB. PDF, Word, Excel, PowerPoint, images.
          </span>
        </div>
      )}

      {message && (
        <div className="alert alert-success" style={{ marginBottom: '12px' }}>
          {message}
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {docs.length === 0 && hasDataroom && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '13px' }}>
            No documents uploaded yet.
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden' }}>
          {docs.map((docItem, i) => (
            <div key={docItem.id} style={{
              padding: '10px 14px',
              borderBottom: i < docs.length - 1 ? '1px solid var(--color-border-light)' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'var(--color-card)',
            }}>
              <div>
                <div style={{ fontSize: '13px', color: 'var(--color-text)', fontWeight: 500 }}>
                  {docItem.file_name}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                  {docItem.uploaded_by_name || 'Unknown'} &middot;{' '}
                  {docItem.uploaded_at
                    ? new Date(docItem.uploaded_at).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })
                    : '—'}
                </div>
              </div>
              {docItem.drive_view_link && (
                <a
                  href={docItem.drive_view_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-card)',
                    color: 'var(--color-text-secondary)',
                    fontSize: '12px',
                    textDecoration: 'none',
                  }}
                >
                  View
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
